import type { PatchRejectionEntry } from "@repo/shared/types";
import { Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api/_client";
import {
  useCombinedInstrument,
  useCreateCombinedInstrument,
  useUpdateCombinedInstrument,
} from "@/lib/hooks/use-combined-instruments";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import {
  InstrumentSearchDialog,
  type InstrumentSelectResult,
} from "@/pages/settings/portfolios/detail/products/instrument-search-dialog";
import { InstrumentSyncAlert } from "@/pages/settings/portfolios/detail/products/instrument-sync-status";
import {
  CombinedInstrumentAllocationRow,
  type InstrumentWithDetails,
} from "./combined-instrument-allocation-row";
import { T } from "./combined-instrument-dialog.i18n";

const CURRENCY_OPTIONS = ["NOK", "SEK", "DKK", "EUR", "USD", "GBP", "CHF"];

type CombinedInstrumentDialogProps = {
  open: boolean;
  editingId: string | null;
  onClose: () => void;
};

export function CombinedInstrumentDialog({
  open,
  editingId,
  onClose,
}: CombinedInstrumentDialogProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const { data: editingInstrument, isLoading: isLoadingInstrument } = useCombinedInstrument(
    open ? editingId : null,
  );
  const createMutation = useCreateCombinedInstrument();
  const updateMutation = useUpdateCombinedInstrument();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [formData, setFormData] = useState({
    name: "",
    isin: "",
    currencyIso: "",
  });
  const [instrumentRows, setInstrumentRows] = useState<InstrumentWithDetails[]>([]);
  const [error, setError] = useState<string | null>(null);
  /**
   * The server's per-leg patch rejections from the last refused save, keyed by
   * leg index — the save refuses every failing leg at once, so all of them are
   * marked from one response. Cleared whenever the legs change, since an index
   * from the previous shape would point at the wrong row.
   */
  const [patchErrors, setPatchErrors] = useState<Record<number, string>>({});
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [patchingInstrument, setPatchingInstrument] = useState<InstrumentWithDetails | null>(null);
  const [formInitializationCompletedInEditMode, setFormInitializationCompletedInEditMode] =
    useState(false);

  useEffect(() => {
    if (editingInstrument) {
      setFormData({
        name: editingInstrument.name,
        isin: editingInstrument.isin ?? "",
        currencyIso: editingInstrument.currencyIso,
      });
      const instrumentsWithDetails: InstrumentWithDetails[] = editingInstrument.instruments.map(
        (item) => {
          const [dataset, ticker] = item.instrumentId.split("+");
          return {
            instrumentId: item.instrumentId,
            weight: item.weight,
            patchInstrumentId: item.patchInstrumentId,
            hedged: item.hedged ?? false,
            displayName: ticker || item.instrumentId,
            displayTicker: ticker || "",
            displayDataset: dataset || "",
            mainInstrumentFirstDate: "",
            mainInstrumentLastDate: "",
          };
        },
      );
      setInstrumentRows(instrumentsWithDetails);
      setFormInitializationCompletedInEditMode(true);
    } else if (!editingId) {
      setFormData({ name: "", isin: "", currencyIso: "" });
      setInstrumentRows([]);
      setFormInitializationCompletedInEditMode(false);
    } else {
      // editingId is set but data hasn't arrived yet — reset flag
      setFormInitializationCompletedInEditMode(false);
    }
    setError(null);
    setPatchErrors({});
  }, [editingInstrument, editingId, open]);

  const handleInstrumentSelect = (result: InstrumentSelectResult) => {
    const { instrument: selectedInstrument, dataset, metadata } = result;
    const instrumentId = `${dataset}+${selectedInstrument.ticker}`;

    if (instrumentRows.some((item) => item.instrumentId === instrumentId)) {
      toast.error(t.instrumentAlreadyAdded);
      return;
    }

    const newInstrument: InstrumentWithDetails = {
      instrumentId,
      weight: 0,
      patchInstrumentId: null,
      // Conversion is the right treatment for an ordinary foreign-currency
      // instrument; hedging is the exception the designer opts into.
      hedged: false,
      displayName: metadata?.name || selectedInstrument.name,
      displayTicker: selectedInstrument.ticker,
      displayDataset: dataset,
      currency: metadata?.currency ?? null,
      mainInstrumentFirstDate: metadata?.firstdate ?? "",
      mainInstrumentLastDate: metadata?.lastdate ?? "",
    };

    setPatchErrors({});
    setInstrumentRows([...instrumentRows, newInstrument]);
    setSearchDialogOpen(false);
  };

  const handleRemoveInstrument = (index: number) => {
    setPatchErrors({});
    setInstrumentRows(instrumentRows.filter((_, i) => i !== index));
  };

  const handleWeightChange = (index: number, value: string) => {
    const updated = [...instrumentRows];
    updated[index] = { ...updated[index], weight: Number(value) || 0 };
    setInstrumentRows(updated);
  };

  // Stable: the row calls this from an effect when its leg lands in the basket currency.
  const handleHedgedChange = useCallback((index: number, hedged: boolean) => {
    setInstrumentRows((prev) => prev.map((row, i) => (i === index ? { ...row, hedged } : row)));
  }, []);

  const handlePatchSelect = (result: InstrumentSelectResult) => {
    if (!patchingInstrument) return;

    const { instrument: selected, dataset, metadata } = result;
    const patchId = `${dataset}+${selected.ticker}`;
    setPatchErrors({});
    setInstrumentRows((prev) =>
      prev.map((item) =>
        item.instrumentId === patchingInstrument.instrumentId
          ? {
              ...item,
              patchInstrumentId: patchId,
              patchDisplayName: metadata?.name || selected.name,
              patchTicker: selected.ticker,
              patchDataset: dataset,
              patchCurrency: metadata?.currency ?? null,
              patchFirstDate: metadata?.firstdate ?? null,
              patchLastDate: metadata?.lastdate ?? null,
            }
          : item,
      ),
    );
    setPatchingInstrument(null);
  };

  const handleRemovePatch = (item: InstrumentWithDetails) => {
    setPatchErrors({});
    setInstrumentRows((prev) =>
      prev.map((i) =>
        i.instrumentId === item.instrumentId
          ? {
              ...i,
              patchInstrumentId: null,
              patchDisplayName: null,
              patchTicker: null,
              patchDataset: null,
              patchCurrency: null,
              patchFirstDate: null,
              patchLastDate: null,
            }
          : i,
      ),
    );
  };

  const totalWeight = instrumentRows.reduce((sum, item) => sum + item.weight, 0);
  const isValidWeight = Math.abs(totalWeight - 100) < 0.01;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPatchErrors({});

    if (instrumentRows.length < 2) {
      setError(t.minInstruments);
      return;
    }

    if (!isValidWeight) {
      setError(t.weightError);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        isin: formData.isin,
        currencyIso: formData.currencyIso,
        instruments: instrumentRows.map((item) => ({
          instrumentId: item.instrumentId,
          weight: item.weight,
          patchInstrumentId: item.patchInstrumentId || null,
          hedged: item.hedged,
        })),
      };

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.failedToSave);

      // A refused patch also gets pinned to the leg it belongs to, so the
      // designer is not left matching a message against a table by eye. The
      // banner keeps the summary for everything else (weights, ISIN, name).
      const patches = err instanceof ApiError ? (err.body as { patches?: unknown })?.patches : null;
      if (Array.isArray(patches)) {
        setPatchErrors(
          Object.fromEntries(
            (patches as PatchRejectionEntry[]).map((p) => [p.legIndex, p.message]),
          ),
        );
      }
    }
  };

  const isEditing = !!editingId;

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogScrollContent className="max-w-4xl">
          <DialogScrollHeader>
            <DialogTitle>{isEditing ? t.editTitle : t.createTitle}</DialogTitle>
            <DialogDescription>
              {isEditing ? t.editDescription : t.createDescription}
            </DialogDescription>
          </DialogScrollHeader>

          {isEditing && (isLoadingInstrument || !formInitializationCompletedInEditMode) ? (
            <DialogBody>
              <div className="py-8 text-center text-muted-foreground">{t.loading}</div>
            </DialogBody>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
              <DialogBody className="space-y-4">
                {/* Why the last recompute failed, in front of the legs a designer is about to fix (QL-333). */}
                {isEditing && (
                  <InstrumentSyncAlert
                    health={editingInstrument?.instrumentSync ?? null}
                    lang={userLanguage}
                    variant="recompute"
                  />
                )}
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t.name} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t.namePlaceholder}
                    required
                    minLength={5}
                    maxLength={255}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">{t.nameHelp}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="isin">
                    {t.isin} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="isin"
                    value={formData.isin}
                    onChange={(e) =>
                      setFormData({ ...formData, isin: e.target.value.toUpperCase().slice(0, 12) })
                    }
                    placeholder={t.isinPlaceholder}
                    required
                    maxLength={12}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">{t.isinHelp}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">
                    {t.currency} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.currencyIso}
                    onValueChange={(value) => setFormData({ ...formData, currencyIso: value })}
                    disabled={isEditing || isPending}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t.selectCurrency} />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>{t.instruments}</Label>
                      <p className="text-xs text-muted-foreground">{t.instrumentsHelp}</p>
                    </div>
                    <Button
                      data-dialog-guard-ignore
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSearchDialogOpen(true)}
                      disabled={isPending}
                    >
                      {t.addInstrument}
                    </Button>
                  </div>

                  {instrumentRows.length > 0 ? (
                    <div className="rounded-md border">
                      <Table variant="data" surface={false}>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t.instrumentName}</TableHead>
                            <TableHead>{t.patchInstrument}</TableHead>
                            <TableHead className="w-24" title={t.hedgedHelp}>
                              {t.hedged}
                            </TableHead>
                            <TableHead className="w-32">{t.weight}</TableHead>
                            <TableHead className="w-16" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {instrumentRows.map((item, index) => (
                            <CombinedInstrumentAllocationRow
                              key={item.instrumentId}
                              item={item}
                              index={index}
                              isEditMode={isEditing}
                              onWeightChange={handleWeightChange}
                              onHedgedChange={handleHedgedChange}
                              basketCurrency={formData.currencyIso || null}
                              onRemove={handleRemoveInstrument}
                              onPatchSelect={setPatchingInstrument}
                              onPatchRemove={handleRemovePatch}
                              isPending={isPending}
                              patchError={patchErrors[index]}
                            />
                          ))}
                          <TableRow>
                            <TableCell className="font-medium">{t.totalWeight}</TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell
                              className={`font-medium ${isValidWeight ? "text-success" : "text-destructive"}`}
                            >
                              {totalWeight.toFixed(2)}%
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-8 border rounded-md">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>{t.noInstruments}</p>
                    </div>
                  )}
                </div>
              </DialogBody>

              <DialogScrollFooter>
                <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
                <Button type="submit" loading={isPending}>
                  {isEditing ? t.save : t.addCombinedInstrument}
                </Button>
              </DialogScrollFooter>
            </form>
          )}
        </DialogScrollContent>
      </Dialog>

      <InstrumentSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSelect={handleInstrumentSelect}
        disableCustomInstruments
        disableCombinedInstruments
      />

      <InstrumentSearchDialog
        open={patchingInstrument !== null}
        isPatchMode
        linkedInstrument={
          patchingInstrument
            ? {
                ticker: patchingInstrument.displayTicker,
                dataset: patchingInstrument.displayDataset,
              }
            : null
        }
        onClose={() => setPatchingInstrument(null)}
        onSelect={handlePatchSelect}
        disableCustomInstruments
        disableCombinedInstruments
      />
    </>
  );
}
